/**
 * Pre-order checkout gate.
 *
 * When the cart contains pre-order items, the cart surfaces mark their checkout
 * area with [data-preorder-scope]. Any checkout control clicked inside it — our
 * Checkout button, the links Route's preferred-checkout widget injects
 * ("Checkout without Coverage"), or the transparent
 * [data-preorder-express-shield] over the express checkout buttons — opens the
 * disclaimer modal instead of checking out. The customer must tick the
 * acknowledgement checkbox to unlock "Continue to Checkout", which then replays
 * the click on the control they originally used.
 *
 * Acknowledgement is held in memory as a class on <body>, so it survives the
 * cart drawer re-rendering itself but is asked again on a fresh page load —
 * this is a shipping-policy agreement, not a saved preference.
 */
const ACK_CLASS = 'preorder-acknowledged';
const CLICKABLE = 'a, button, small, input[type="submit"], [role="button"]';

let pendingTrigger = null;
let lastFocused = null;

const getModal = () => document.querySelector('[data-preorder-modal]');

const isAcknowledged = () => document.body.classList.contains(ACK_CLASS);

// The cart drawer also locks body scroll; don't release its lock when the
// modal closes on top of an open drawer.
function releaseScrollLock() {
  document.body.style.overflow = document.body.classList.contains('cart-open') ? 'hidden' : '';
}

function openModal(trigger) {
  const modal = getModal();
  if (!modal) return false;

  pendingTrigger = trigger;
  lastFocused = document.activeElement;

  const checkbox = modal.querySelector('[data-preorder-accept]');
  const confirm = modal.querySelector('[data-preorder-confirm]');
  if (checkbox) checkbox.checked = false;
  if (confirm) {
    confirm.disabled = true;
    // Express checkout has nothing to replay — accepting only unlocks the
    // payment buttons, so don't promise to take them to checkout.
    confirm.textContent = trigger ? confirm.dataset.label : confirm.dataset.labelExpress;
  }

  modal.classList.add('is-open');
  modal.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';

  setTimeout(() => checkbox?.focus(), 50);
  return true;
}

function closeModal() {
  const modal = getModal();
  if (!modal?.classList.contains('is-open')) return;

  modal.classList.remove('is-open');
  modal.setAttribute('aria-hidden', 'true');
  pendingTrigger = null;
  releaseScrollLock();
  lastFocused?.focus();
}

// Route renders its checkout links with its own markup (possibly inside its web
// component), so recognise checkout controls by what they do, not by class.
function isCheckoutControl(el) {
  if (el.matches('[data-preorder-gate]')) return true;
  if (!el.matches(CLICKABLE)) return false;

  const hint = `${el.getAttribute('href') || ''} ${el.getAttribute('name') || ''} ${el.textContent}`;
  return /check\s?out/i.test(hint);
}

// Returns the checkout control the click landed on, but only when it sits
// inside a pre-order cart's checkout area.
function findGatedControl(e) {
  let control = null;

  for (const node of e.composedPath()) {
    if (!(node instanceof Element)) continue;
    if (node.matches('[data-preorder-scope]')) return control;
    if (!control && (node.matches('[data-preorder-express-shield]') || isCheckoutControl(node))) {
      control = node;
    }
  }

  return null;
}

// Capture phase on window runs before any listener bound to the control itself.
// Route's preferred-checkout widget attaches its own click handler to the
// checkout buttons and redirects straight to checkout, so a bubbling listener
// would never get the chance to open the modal.
window.addEventListener(
  'click',
  (e) => {
    if (isAcknowledged() || !getModal()) return;

    const control = findGatedControl(e);
    if (!control) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    openModal(control.matches('[data-preorder-express-shield]') ? null : control);
  },
  true
);

document.addEventListener('click', (e) => {
  if (!(e.target instanceof Element)) return;

  if (e.target.closest('[data-preorder-confirm]')) {
    const modal = getModal();
    if (!modal?.querySelector('[data-preorder-accept]')?.checked) return;

    document.body.classList.add(ACK_CLASS);
    let trigger = pendingTrigger;
    // The drawer may have re-rendered while the modal was open.
    if (trigger && !trigger.isConnected) trigger = document.querySelector('[data-preorder-gate]');
    closeModal();
    // Replay the original click so Route (and the form/link itself) handles
    // checkout exactly as it would have without the gate.
    trigger?.click();
    return;
  }

  if (e.target.closest('[data-preorder-modal-close]') || e.target.closest('[data-preorder-backdrop]')) {
    closeModal();
  }
});

document.addEventListener('change', (e) => {
  const checkbox = e.target.closest('[data-preorder-accept]');
  if (!checkbox) return;

  const confirm = getModal()?.querySelector('[data-preorder-confirm]');
  if (confirm) confirm.disabled = !checkbox.checked;
});

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeModal();
});
